import { Grid } from '@mui/joy'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { IMask } from 'react-imask'
import { FormValues } from '~/entities/RoadAccident'
import { TMS_DOMAIN } from '~/shared/config/env'
import { LinkDecorator } from '~/shared/ui/Buttons'
import {
  DatePickerInput,
  NumericInput,
  Switch,
  TextInput,
  TextareaInput,
  MaskInput,
} from '~/shared/ui/Form'
import { Container, ContainerTitle } from '~/shared/ui/Layout'

type MainProps = {
  disabled: boolean
  loading: boolean
}

export function Main({ disabled, loading }: MainProps) {
  const { t } = useTranslation()
  const { watch } = useFormContext<FormValues>()

  const carExtId = watch('carExtId')
  const inspectionId = watch('inspectionId')

  return (
    <Container>
      <ContainerTitle>{t('form.information')}</ContainerTitle>
      <Grid container spacing={2.5}>
        <Grid xs={6}>
          <DatePickerInput
            label='form.creation date'
            placeholder='form.creation date'
            name='createdAt'
            skeletonShow={loading}
            readOnly
          />
        </Grid>
        <Grid xs={3}>
          <DatePickerInput
            label='form.accident date'
            placeholder='form.select accident date'
            name='accidentDate'
            skeletonShow={loading}
            readOnly={disabled}
          />
        </Grid>
        <Grid xs={3}>
          <MaskInput
            label='form.accident time'
            placeholder='form.enter accident time'
            name='accidentTime'
            readOnly={disabled}
            skeletonShow={loading}
            mask='HH:MM'
            blocks={{
              HH: {
                mask: IMask.MaskedRange,
                from: 0,
                to: 23,
              },
              MM: {
                mask: IMask.MaskedRange,
                from: 0,
                to: 59,
              },
            }}
          />
        </Grid>

        <Grid xs={2}>
          <Switch
            labelI18n='form.evacuation'
            name='evacuation'
            readOnly={disabled}
            skeletonShow={loading}
            formControlProps={{ sx: { marginTop: '40px' } }}
          />
        </Grid>
        <Grid xs={3}>
          <Switch
            labelI18n='form.agreement with driver'
            name='agreementWithDriver'
            readOnly={disabled}
            skeletonShow={loading}
            formControlProps={{ sx: { marginTop: '40px' } }}
          />
        </Grid>
        <Grid xs={7}>
          <TextInput
            label='form.accident place'
            placeholder='form.specify accident place'
            name='accidentPlace'
            readOnly={disabled}
            skeletonShow={loading}
          />
        </Grid>

        <Grid xs={4}>
          <TextInput
            label='form.vehicle'
            placeholder='form.vehicle'
            name='carPlateNumber'
            readOnly
            skeletonShow={loading}
            endDecorator={
              carExtId && (
                <LinkDecorator
                  tooltipText={t('form.go to car card')}
                  href={`${TMS_DOMAIN}cars/${carExtId}`}
                />
              )
            }
          />
        </Grid>
        <Grid xs={4}>
          <TextInput
            label='form.vehicle subdivision'
            placeholder='form.vehicle subdivision'
            name='carSubdivision'
            readOnly
            skeletonShow={loading}
          />
        </Grid>
        <Grid xs={4}>
          <TextInput
            label='form.vehicle status'
            placeholder='form.vehicle status'
            name='carStatus'
            readOnly
            skeletonShow={loading}
          />
        </Grid>

        <Grid xs={4}>
          <TextInput
            label='form.driver full name'
            placeholder='form.driver full name'
            name='driverFullName'
            readOnly
            skeletonShow={loading}
          />
        </Grid>
        <Grid xs={4}>
          <TextInput
            label='form.vehicle inspection'
            placeholder='form.specify vehicle inspection'
            name='inspection'
            readOnly
            skeletonShow={loading}
            endDecorator={
              inspectionId && (
                <LinkDecorator
                  tooltipText={t('form.go to car card')}
                  to={`/inspections/${inspectionId}`}
                />
              )
            }
          />
        </Grid>
        <Grid xs={4}>
          <NumericInput
            name='mileage'
            placeholder='column.mileage'
            label='column.mileage'
            skeletonShow={loading}
            readOnly
          />
        </Grid>

        <Grid xs={6} container>
          <Grid xs={12}>
            <NumericInput
              name='repairEstimatedCost'
              label='form.estimated repair cost'
              placeholder='form.estimated repair cost'
              skeletonShow={loading}
              readOnly
            />
          </Grid>
          <Grid xs={12}>
            <DatePickerInput
              label='form.debt closing date'
              placeholder='form.select debt closing date'
              name='debtClosingDate'
              skeletonShow={loading}
              readOnly={disabled}
            />
          </Grid>
        </Grid>
        <Grid xs={6}>
          <TextareaInput
            label='form.comment'
            name='comment'
            placeholder='form.enter comment'
            sx={{
              height: '138px',
            }}
            skeletonShow={loading}
            readOnly={disabled}
          />
        </Grid>

        <Grid xs={4}>
          <NumericInput
            label='form.reimbursed by driver'
            name='reimbursedByDriver'
            placeholder='form.enter amount reimbursed by driver'
            skeletonShow={loading}
            readOnly={disabled}
          />
        </Grid>
        <Grid xs={4}>
          <NumericInput
            label='form.reimbursed by culprit'
            name='reimbursedByCulprit'
            placeholder='form.enter amount reimbursed by culprit'
            skeletonShow={loading}
            readOnly={disabled}
          />
        </Grid>
        <Grid xs={4}>
          <NumericInput
            label='form.remaining amount'
            placeholder='form.remaining amount'
            name='remainingAmount'
            skeletonShow={loading}
            readOnly
          />
        </Grid>
      </Grid>
    </Container>
  )
}
